import React, { useEffect, useState } from 'react';
import { Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import EmailRecipientFields, { emptyRoutingState, routingApiPayload } from './EmailRecipientFields';

/**
 * Voransicht für Dokument-Mails (PDF-iframe): Empfängeradresse sichtbar + extra An/CC/BCC.
 */
export default function DocumentEmailDialog({
  open,
  onClose,
  onConfirm,
  confirming = false,
  title,
  description = 'Bitte PDF prüfen. Versand erst nach Bestätigung.',
  recipient,
  recipientLabel,
  pdfUrl,
  testId = 'document-email-dialog',
  confirmTestId,
  iframeTitle,
}) {
  const [routing, setRouting] = useState(emptyRoutingState());

  useEffect(() => {
    if (open) setRouting(emptyRoutingState());
  }, [open, recipient]);

  const hasRecipient = !!(recipient || '').trim();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !confirming) onClose(); }}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col" data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 shrink-0 text-sm">
          <div data-testid={`${testId}-to`}>
            <span className="text-slate-500">An:</span>{' '}
            {hasRecipient ? (
              <>
                {recipientLabel ? <span className="font-medium text-slate-800">{recipientLabel}</span> : null}
                {recipientLabel ? <span className="text-slate-500"> · </span> : null}
                <span className={recipientLabel ? 'text-slate-600' : 'font-medium text-slate-800'}>{recipient}</span>
              </>
            ) : (
              <span className="font-medium text-amber-800">Keine E-Mail-Adresse hinterlegt — Versand nicht möglich</span>
            )}
          </div>
          <EmailRecipientFields
            extraTo={routing.extraTo}
            cc={routing.cc}
            bcc={routing.bcc}
            onChange={setRouting}
            disabled={confirming}
            testId={`${testId}-routing`}
          />
        </div>

        {pdfUrl ? (
          <iframe
            src={pdfUrl}
            title={iframeTitle || title}
            className="flex-1 w-full min-h-0 rounded border border-slate-200"
            data-testid={`${testId}-iframe`}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-slate-400">Keine Voransicht</div>
        )}

        <DialogFooter className="gap-2 sm:gap-0 shrink-0">
          <Button variant="outline" onClick={onClose} disabled={confirming}>Abbrechen</Button>
          <Button
            onClick={() => onConfirm(routingApiPayload(routing))}
            disabled={confirming || !hasRecipient}
            data-testid={confirmTestId || `${testId}-confirm`}
          >
            {confirming ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
            Jetzt senden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
